import {
  TrackedShipmentPosition,
  TrackedShipmentStop,
} from '@project44-models/shipment-entity';

import {
  getShipmentPositions,
  getShipmentRoutePositions,
  getStopMarkerLabel,
  MapMarker,
  Position,
} from '../../../../utils/helpers/shipmentMap';
import { RouteIcon } from '../../../RouteIcons';
import Marker from './components/Marker';
import { drawMarker, drawPoints, drawRoute, fitBounds } from './draw';

import { CurrentPosition } from './components/Marker/styles';

const drawShipmentRoute = (
  map: mapboxgl.Map,
  routeSegments: Position[][],
  shipmentPositions: TrackedShipmentPosition[],
  onChangeActivePing: (p: TrackedShipmentPosition) => void,
) => {
  const data = getShipmentRoutePositions(shipmentPositions);
  const line = data.map(({ lon, lat }) => [lon, lat]);

  drawRoute(map, line as [number, number][], -1);
  routeSegments.forEach((segment, idx: number) => {
    drawPoints(map, segment, idx, onChangeActivePing);
  });
};

const drawShipmentRouteMarkers = (
  map: mapboxgl.Map,
  markers: MapMarker[],
  stops: TrackedShipmentStop[],
  routeIcon: string,
  currentPosition?: TrackedShipmentPosition,
) => {
  for (const marker of markers) {
    const { lat, lng, id, type, isCurrent } = marker;
    const stopNumber = stops.findIndex((stop) => stop.id === id);
    const label = getStopMarkerLabel(stops[stopNumber], stopNumber);

    const element = (
      <Marker
        isCurrent={isCurrent}
        routeIcon={routeIcon}
        label={label}
        icon={type}
      />
    );

    drawMarker(map, element, [lng, lat]);
  }

  if (currentPosition) {
    const { latitude: lat, longitude: lng } = currentPosition;

    const element = (
      <CurrentPosition>
        <RouteIcon name={routeIcon} />
      </CurrentPosition>
    );

    drawMarker(map, element, [Number(lng), Number(lat)]);
  }
};

const fitMapBounds = (
  map: mapboxgl.Map,
  markers: MapMarker[],
  stops: TrackedShipmentStop[],
  routeSegments: Position[][],
) => {
  const maxMarkerLabelLength = Math.max(
    ...markers.map((m) => {
      const stopNumber = stops.findIndex((stop) => stop.id === m.id);
      return getStopMarkerLabel(stops[stopNumber], stopNumber).length;
    }),
  );

  const routeCoords = routeSegments.flatMap((segment) =>
    segment.map(({ lon, lat }) => [lon, lat]),
  );

  const stopsCoords = markers.map((i) => [i.lng, i.lat]);
  const allCoords = [...routeCoords, ...stopsCoords] as [number, number][];

  fitBounds(map, allCoords, maxMarkerLabelLength);
};

export const drawShipmentMapElements = (
  map: mapboxgl.Map,
  markers: MapMarker[],
  currentPosition: TrackedShipmentPosition | undefined,
  stops: TrackedShipmentStop[],
  shipmentPositions: TrackedShipmentPosition[],
  onChangeActivePing: (p: TrackedShipmentPosition) => void,
  routeIcon: string,
) => {
  const routeSegments = getShipmentPositions(shipmentPositions, false);

  drawShipmentRoute(map, routeSegments, shipmentPositions, onChangeActivePing);
  drawShipmentRouteMarkers(map, markers, stops, routeIcon, currentPosition);
  fitMapBounds(map, markers, stops, routeSegments);
};
